/**
 * Knowledge Harvest Button Component
 *
 * Triggers the knowledge synthesis workflow to analyze the codebase
 * and extract patterns, gaps, and insights.
 *
 * Now supports two modes:
 * 1. Knowledge Harvest (legacy) - general knowledge synthesis
 * 2. Generate from Codebase - generates specifications from codebase scan results
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState } from 'react';
import { Button, Spinner, Badge } from 'flowbite-react';
import { HiLightningBolt, HiCheckCircle, HiExclamationCircle, HiDocumentText } from 'react-icons/hi';

interface KnowledgeHarvestButtonProps {
  onHarvestComplete: (result: KnowledgeSynthesisResult) => void;
  onSpecsGenerated?: (result: GeneratedSpecsResult) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showStatus?: boolean;
  // For spec generation mode
  mode?: 'harvest' | 'generate-specs';
  clientId?: string;
  projectId?: string;
  repoUrl?: string;
  scanId?: string;
}

export interface KnowledgeSynthesisResult {
  synthesis_id: string;
  timestamp: string;
  patterns: Pattern[];
  gaps: KnowledgeGap[];
  insights: StrategyInsight[];
  recommendations: Recommendation[];
  statistics: {
    patterns_count: number;
    gaps_count: number;
    insights_count: number;
    recommendations_count: number;
  };
}

export interface Pattern {
  type: string;
  name?: string;
  title?: string;
  description?: string;
  context?: string;
  confidence?: number;
  frequency?: number;
  examples?: string[];
  sentiment?: 'positive' | 'negative' | 'neutral';
}

export interface KnowledgeGap {
  type: string;
  title?: string;
  description: string;
  severity?: 'low' | 'medium' | 'high';
  priority?: 'low' | 'medium' | 'high';
  recommendation?: string;
  location?: string;
}

export interface StrategyInsight {
  type: string;
  title: string;
  description: string;
  impact?: 'low' | 'medium' | 'high';
  recommendation?: string;
  confidence?: number;
}

export interface Recommendation {
  priority: 'low' | 'medium' | 'high';
  category: string;
  title: string;
  description: string;
  action: string;
}

// Result type for spec generation from codebase
export interface GeneratedSpecsResult {
  Records: GeneratedSpec[];
  Total_Records: number;
  Success_Count: number;
  Failure_Count: number;
  Scan_ID: string;
  Scan_Business_ID: string;
  Repository: string;
  Spec_Types_Generated: string[];
  Generation_Results: GenerationResult[];
}

export interface GeneratedSpec {
  id: string;
  business_id: string;
  title: string;
  status: string;
  tags: string[];
  version: number;
  created_at: string;
  connected_repo: string;
}

export interface GenerationResult {
  spec_type: string;
  spec_id: string;
  business_id: string;
  title: string;
  status: 'completed' | 'failed';
  error?: string;
}

const KnowledgeHarvestButton: React.FC<KnowledgeHarvestButtonProps> = ({
  onHarvestComplete,
  onSpecsGenerated,
  onError,
  disabled = false,
  variant = 'primary',
  size = 'md',
  showStatus = true,
  mode = 'harvest',
  clientId,
  projectId,
  repoUrl,
  scanId
}) => {
  const [loading, setLoading] = useState(false);
  const [lastHarvest, setLastHarvest] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generatedCount, setGeneratedCount] = useState<number | null>(null);

  const handleHarvest = async () => {
    try {
      setLoading(true);
      setError(null);
      setGeneratedCount(null);

      // Import API utilities dynamically to avoid circular dependencies
      const { Make_Authorized_API_Call } = await import('../services/api/utils');
      const { API_ENDPOINTS } = await import('../services/api/endpoints');

      console.log('[KnowledgeHarvest] Starting knowledge synthesis...');

      const result = await Make_Authorized_API_Call<KnowledgeSynthesisResult>(
        API_ENDPOINTS.KNOWLEDGE.HARVEST,
        'POST',
        {
          includePatterns: true,
          includeGaps: true,
          includeInsights: true,
          crossProjectAnalysis: false,
          synthesisType: 'comprehensive',
          depth: 'comprehensive'
        }
      );

      if (result.success && result.data) {
        console.log('[KnowledgeHarvest] Synthesis complete:', {
          synthesis_id: result.data.synthesis_id,
          statistics: result.data.statistics
        });

        setLastHarvest(new Date());
        onHarvestComplete(result.data);
      } else {
        throw new Error(result.message || 'Knowledge synthesis failed');
      }
    } catch (err: any) {
      console.error('[KnowledgeHarvest] Error:', err);
      const errorMessage = err.message || 'Failed to harvest knowledge';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSpecs = async () => {
    if (!clientId) {
      setError('Client ID is required for spec generation');
      return;
    }

    if (!repoUrl && !scanId) {
      setError('Repository URL or Scan ID is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setGeneratedCount(null);

      const { Make_Authorized_API_Call } = await import('../services/api/utils');
      const { API_ENDPOINTS } = await import('../services/api/endpoints');

      console.log('[KnowledgeHarvest] Generating specs from codebase...', {
        clientId,
        projectId,
        repoUrl,
        scanId
      });

      const result = await Make_Authorized_API_Call<GeneratedSpecsResult>(
        API_ENDPOINTS.SPECIFICATIONS.GENERATE_FROM_CODEBASE,
        'POST',
        {
          Client_ID: clientId,
          Project_ID: projectId,
          repo_url: repoUrl,
          scan_id: scanId
        }
      );

      if (result.success && result.data) {
        console.log('[KnowledgeHarvest] Specs generated:', {
          totalRecords: result.data.Total_Records,
          successCount: result.data.Success_Count,
          specTypes: result.data.Spec_Types_Generated
        });

        setLastHarvest(new Date());
        setGeneratedCount(result.data.Success_Count || result.data.Records?.length || 0);

        if (onSpecsGenerated) {
          onSpecsGenerated(result.data);
        }
      } else {
        throw new Error(result.message || 'Spec generation failed');
      }
    } catch (err: any) {
      console.error('[KnowledgeHarvest] Error:', err);
      const errorMessage = err.message || 'Failed to generate specifications';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (mode === 'generate-specs') {
      handleGenerateSpecs();
    } else {
      handleHarvest();
    }
  };

  const buttonColors = {
    primary: 'blue',
    secondary: 'gray',
    ghost: 'light'
  };

  const isGenerateMode = mode === 'generate-specs';

  const buttonText = isGenerateMode
    ? (loading ? 'Generating Specs...' : 'Generate Specs from Codebase')
    : (loading ? 'Analyzing Codebase...' : 'Generate from Codebase');

  const ButtonIcon = isGenerateMode ? HiDocumentText : HiLightningBolt;

  return (
    <div className="flex items-center space-x-2">
      <Button
        color={buttonColors[variant]}
        size={size}
        onClick={handleClick}
        disabled={disabled || loading}
      >
        {loading ? (
          <>
            <Spinner size="sm" className="mr-2" />
            {buttonText}
          </>
        ) : (
          <>
            <ButtonIcon className="w-4 h-4 mr-2" />
            {buttonText}
          </>
        )}
      </Button>

      {showStatus && !loading && lastHarvest && (
        <div className="flex items-center space-x-1">
          <HiCheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm text-gray-500">
            {isGenerateMode && generatedCount !== null
              ? `Generated ${generatedCount} spec(s) at ${lastHarvest.toLocaleTimeString()}`
              : `Last harvest: ${lastHarvest.toLocaleTimeString()}`
            }
          </span>
        </div>
      )}

      {showStatus && error && (
        <div className="flex items-center space-x-1">
          <HiExclamationCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-500">
            {error}
          </span>
        </div>
      )}
    </div>
  );
};

export default KnowledgeHarvestButton;
